import React, { useState, useEffect } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Users,
  ChevronDown,
  Link as LinkIcon,
  AlertTriangle,
  UserPlus,
  UserCheck,
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import { referralBonusService } from "../../lib/referralBonusService";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Alert from "../ui/Alert";
import ConfirmationModal from "../ui/ConfirmationModal";

const ReferralManager = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [referrals, setReferrals] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [existingAffiliates, setExistingAffiliates] = useState([]);
  const [showReferrerDropdown, setShowReferrerDropdown] = useState(false);
  const [showRefereeDropdown, setShowRefereeDropdown] = useState(false);
  const [formData, setFormData] = useState({
    referrer_name: "",
    referee_name: "",
  });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [referralToDelete, setReferralToDelete] = useState(null);
  const [isCalculatingBonuses, setIsCalculatingBonuses] = useState(false);
  const [bonusCalculationResult, setBonusCalculationResult] = useState(null);

  // Load referrals and existing affiliates on component mount
  useEffect(() => {
    loadReferrals();
    loadExistingAffiliates();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setShowReferrerDropdown(false);
        setShowRefereeDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const loadExistingAffiliates = async () => {
    try {
      // Load ALL users from profiles table, not just whitelisted ones
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("username")
        .not("username", "is", null)
        .order("username")
        .limit(10000); // Fetch up to 10,000 users to avoid default limit

      if (profilesError) {
        console.error("Error loading users:", profilesError);
        return;
      }

      // Extract usernames and filter out empty ones
      const allUsers = profilesData
        ?.map((item) => item.username?.trim())
        .filter((username) => username && username.length > 0)
        .sort();

      setExistingAffiliates(allUsers || []);
    } catch (err) {
      console.error("Error loading existing affiliates:", err);
    }
  };

  const loadReferrals = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("affiliate_referrals")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setReferrals(data || []);
    } catch (err) {
      setError("Failed to load referral connections: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      clearMessages();

      const { referrer_name, referee_name } = formData;

      // Validation is now primarily handled by the edge function,
      // but we can keep simple client-side checks for better UX.
      if (!referrer_name.trim() || !referee_name.trim()) {
        throw new Error("Both referrer and referee must be selected");
      }

      if (referrer_name === referee_name) {
        throw new Error("Referrer and referee cannot be the same person");
      }

      // Invoke the new edge function
      const { data, error } = await supabase.functions.invoke(
        "manual-referral-create",
        {
          body: { referrer_name, referee_name },
        }
      );

      if (error) {
        // The edge function will return a descriptive error message
        const errorData = await error.context.json();
        throw new Error(errorData.error || "An unknown error occurred.");
      }

      setSuccess(
        `Successfully connected ${referee_name} to ${referrer_name} as referrer`
      );
      await loadReferrals();
      setShowAddForm(false);
      setFormData({ referrer_name: "", referee_name: "" });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (referral) => {
    setReferralToDelete(referral);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!referralToDelete) return;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from("affiliate_referrals")
        .delete()
        .eq("id", referralToDelete.id);

      if (error) throw error;

      setSuccess("Referral connection removed successfully");
      await loadReferrals();
    } catch (err) {
      setError("Failed to delete referral connection: " + err.message);
    } finally {
      setIsLoading(false);
      setIsDeleteModalOpen(false);
      setReferralToDelete(null);
    }
  };

  const cancelForm = () => {
    setShowAddForm(false);
    setEditingId(null);
    setShowReferrerDropdown(false);
    setShowRefereeDropdown(false);
    setFormData({ referrer_name: "", referee_name: "" });
    clearMessages();
  };

  const handleSelectReferrer = (affiliateName) => {
    setFormData((prev) => ({ ...prev, referrer_name: affiliateName }));
    setShowReferrerDropdown(false);
  };

  const handleSelectReferee = (affiliateName) => {
    setFormData((prev) => ({ ...prev, referee_name: affiliateName }));
    setShowRefereeDropdown(false);
  };

  const handleCalculateAllBonuses = async () => {
    try {
      setIsCalculatingBonuses(true);
      clearMessages();
      setBonusCalculationResult(null);

      console.log("🚀 Admin triggered manual referral bonus calculation");

      const result = await referralBonusService.manuallyCalculateAllBonuses();

      setBonusCalculationResult(result);

      if (result.success) {
        if (result.bonusesCalculated > 0) {
          setSuccess(
            `Successfully calculated referral bonuses! ${result.bonusesCalculated} bonuses created totaling $${result.totalBonusAmount.toFixed(2)}`
          );
        } else {
          setSuccess(
            result.message ||
              "Referral bonus calculation completed - no new bonuses to calculate."
          );
        }
      }
    } catch (err) {
      setError("Failed to calculate referral bonuses: " + err.message);
    } finally {
      setIsCalculatingBonuses(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-2xl font-bold text-white">
            Manual Referral Assignment
          </h3>
          <p className="text-gray-300 text-sm sm:text-base">
            Manually connect referrers to referees for 10% referral bonuses
          </p>
        </div>
        <div className="flex gap-2 sm:gap-3 sm:self-end sm:ml-auto">
          <Button
            onClick={handleCalculateAllBonuses}
            disabled={isCalculatingBonuses || isLoading}
            className="bg-green-600 hover:bg-green-700 text-white"
            title="Calculate all pending referral bonuses for the last 12 weeks"
          >
            <Users className="h-4 w-4 mr-2" />
            {isCalculatingBonuses ? "Calculating..." : "Calculate All Bonuses"}
          </Button>
          <Button
            onClick={() => setShowAddForm(true)}
            className="bg-[#B8460E] hover:bg-[#8B3A0E] text-white"
            disabled={showAddForm || editingId}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Referral Connection
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="p-4 bg-blue-500/10 backdrop-blur-xl border border-blue-500/20">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-blue-300 font-semibold mb-1">
              Manual Referral System & Bonus Calculation
            </h4>
            <p className="text-blue-200 text-sm mb-2">
              Use this when automatic referral signup fails. Once connected, the
              referrer will earn 10% bonuses on all future earnings of their
              referee. The 10% is additional income for the referrer - referees
              keep 100% of their earnings.
            </p>
            <p className="text-yellow-200 text-sm font-medium">
              ⚠️ After creating referral connections, click "Calculate All
              Bonuses" to calculate and store all pending referral bonuses. This
              must be done manually for bonuses to appear in payout pages.
            </p>
          </div>
        </div>
      </Card>

      {/* Messages */}
      {error && (
        <Alert type="error" onClose={clearMessages}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert type="success" onClose={clearMessages}>
          {success}
        </Alert>
      )}

      {/* Bonus Calculation Results */}
      {bonusCalculationResult && (
        <Card className="p-6 bg-green-500/10 backdrop-blur-xl border border-green-500/20">
          <h4 className="text-green-300 font-semibold mb-3">
            Referral Bonus Calculation Results
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-black/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-400">
                {bonusCalculationResult.referralConnections}
              </div>
              <div className="text-sm text-green-300">Referral Connections</div>
            </div>
            <div className="bg-black/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-400">
                {bonusCalculationResult.bonusesCalculated}
              </div>
              <div className="text-sm text-green-300">Bonuses Calculated</div>
            </div>
            <div className="bg-black/30 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-400">
                ${bonusCalculationResult.totalBonusAmount?.toFixed(2) || "0.00"}
              </div>
              <div className="text-sm text-green-300">Total Bonus Amount</div>
            </div>
          </div>

          {bonusCalculationResult.bonusSummary &&
            Object.keys(bonusCalculationResult.bonusSummary).length > 0 && (
              <div>
                <h5 className="text-green-200 font-medium mb-2">
                  Bonus Summary by Referrer:
                </h5>
                <div className="space-y-2">
                  {Object.entries(bonusCalculationResult.bonusSummary).map(
                    ([referrer, amounts]) => (
                      <div
                        key={referrer}
                        className="flex justify-between items-center bg-black/30 rounded-lg p-3"
                      >
                        <span className="text-white font-medium">
                          {referrer}
                        </span>
                        <div className="text-right">
                          <div className="text-green-400 font-semibold">
                            ${amounts.unpaid.toFixed(2)} unpaid
                          </div>
                          <div className="text-green-300 text-sm">
                            ${amounts.total.toFixed(2)} total
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

          <div className="mt-4 pt-4 border-t border-green-500/20">
            <p className="text-green-200 text-sm">
              ✅ Bonuses have been calculated and stored. They will now appear
              in affiliate payout pages.
            </p>
          </div>
        </Card>
      )}

      {/* Add Form */}
      {showAddForm && (
        <Card className="relative z-20 p-6 bg-white/10 backdrop-blur-xl border border-white/20">
          <form onSubmit={handleAdd} className="space-y-4">
            <h4 className="text-lg font-semibold text-white mb-4">
              Create Referral Connection
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Referrer Selection */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  <UserCheck className="inline h-4 w-4 mr-1" />
                  Referrer (Who gets 10% bonus)
                </label>
                <div className="relative dropdown-container">
                  <input
                    type="text"
                    value={formData.referrer_name}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        referrer_name: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 pr-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-[#B8460E]/50 focus:border-[#B8460E]/50 transition-all duration-300"
                    placeholder="Select referrer"
                    required
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowReferrerDropdown(!showReferrerDropdown)
                    }
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                  >
                    <ChevronDown className="h-5 w-5" />
                  </button>

                  {showReferrerDropdown && (
                    <div className="absolute z-[9999] w-full mt-1 bg-black/90 backdrop-blur-xl border border-white/20 rounded-xl max-h-48 overflow-y-auto shadow-xl">
                      {existingAffiliates.map((affiliate) => (
                        <button
                          key={affiliate}
                          type="button"
                          onClick={() => handleSelectReferrer(affiliate)}
                          className="w-full px-4 py-2 text-left text-white hover:bg-white/10 transition-colors first:rounded-t-xl last:rounded-b-xl"
                        >
                          {affiliate}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  This person will earn 10% bonuses
                </p>
              </div>

              {/* Referee Selection */}
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">
                  <UserPlus className="inline h-4 w-4 mr-1" />
                  Referee (Who was referred)
                </label>
                <div className="relative dropdown-container">
                  <input
                    type="text"
                    value={formData.referee_name}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        referee_name: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-3 pr-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:ring-2 focus:ring-[#B8460E]/50 focus:border-[#B8460E]/50 transition-all duration-300"
                    placeholder="Select referee"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowRefereeDropdown(!showRefereeDropdown)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white transition-colors"
                  >
                    <ChevronDown className="h-5 w-5" />
                  </button>

                  {showRefereeDropdown && (
                    <div className="absolute z-[9999] w-full mt-1 bg-black/90 backdrop-blur-xl border border-white/20 rounded-xl max-h-48 overflow-y-auto shadow-xl">
                      {existingAffiliates.map((affiliate) => (
                        <button
                          key={affiliate}
                          type="button"
                          onClick={() => handleSelectReferee(affiliate)}
                          className="w-full px-4 py-2 text-left text-white hover:bg-white/10 transition-colors first:rounded-t-xl last:rounded-b-xl"
                        >
                          {affiliate}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  This person's earnings will generate bonuses
                </p>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button
                type="submit"
                disabled={
                  isLoading ||
                  !formData.referrer_name.trim() ||
                  !formData.referee_name.trim()
                }
                className="bg-[#B8460E] hover:bg-[#8B3A0E] text-white"
              >
                <Save className="h-4 w-4 mr-2" />
                {isLoading ? "Creating..." : "Create Connection"}
              </Button>
              <Button
                type="button"
                onClick={cancelForm}
                className="bg-white/10 hover:bg-white/20 text-white border border-white/20"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Referrals Table */}
      <Card className="relative z-10 p-6 bg-black/40 backdrop-blur-xl border border-white/10 shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-white font-semibold">
                  Referrer
                </th>
                <th className="text-left py-3 px-4 text-white font-semibold">
                  Referee
                </th>
                <th className="text-left py-3 px-4 text-white font-semibold">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-white font-semibold">
                  Created
                </th>
                <th className="text-left py-3 px-4 text-white font-semibold">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {referrals.map((referral) => (
                <tr
                  key={referral.id}
                  className="border-b border-white/5 hover:bg-white/5 transition-colors"
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <UserCheck className="h-4 w-4 text-green-400" />
                      <span className="text-white font-medium">
                        {referral.referrer_name}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <UserPlus className="h-4 w-4 text-blue-400" />
                      <span className="text-white font-medium">
                        {referral.referee_name}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        referral.status === "accepted"
                          ? "bg-green-500/20 text-green-300 border border-green-500/30"
                          : referral.status === "pending"
                            ? "bg-yellow-500/20 text-yellow-300 border border-yellow-500/30"
                            : "bg-red-500/20 text-red-300 border border-red-500/30"
                      }`}
                    >
                      {referral.status}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-gray-300 text-sm">
                      {new Date(referral.created_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => handleDelete(referral)}
                      disabled={isLoading}
                      className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-colors disabled:opacity-50"
                      title="Delete connection"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {referrals.length === 0 && !isLoading && (
            <div className="text-center py-8">
              <LinkIcon className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No referral connections found</p>
              <p className="text-gray-500 text-sm">
                Create manual connections to enable referral bonuses
              </p>
            </div>
          )}
        </div>
      </Card>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message={`Are you sure you want to remove the referral connection between ${referralToDelete?.referrer_name} and ${referralToDelete?.referee_name}? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={isLoading}
      />

      {/* How It Works */}
      <Card className="p-6 bg-purple-500/10 backdrop-blur-xl border border-purple-500/20">
        <h4 className="text-lg font-semibold text-purple-300 mb-3">
          How Referral Bonuses Work
        </h4>
        <div className="space-y-2 text-purple-200 text-sm">
          <p>
            • <strong>Step 1:</strong> Create referral connections above
            (Referrer → Referee)
          </p>
          <p>
            • <strong>Step 2:</strong> Click "Calculate All Bonuses" to process
            bonuses
          </p>
          <p>
            • <strong>10% Bonus:</strong> Referrers earn 10% of their referee's
            total weekly earnings
          </p>
          <p>
            • <strong>Additional Income:</strong> The 10% is extra - referees
            keep 100% of their earnings
          </p>
          <p>
            • <strong>Payout Integration:</strong> Bonuses appear in affiliate
            payout pages automatically
          </p>
          <p>
            • <strong>One Referrer Only:</strong> Each affiliate can only have
            one referrer
          </p>
        </div>

        {/* Quick Status Check */}
        <div className="mt-4 pt-4 border-t border-purple-500/20">
          <div className="flex items-center justify-between">
            <span className="text-purple-300 text-sm font-medium">
              Quick Status:
            </span>
            <div className="text-right">
              <div className="text-purple-200 text-sm">
                {referrals.filter((r) => r.status === "accepted").length} Active
                Connections
              </div>
              <div className="text-purple-400 text-xs">
                Last updated: {new Date().toLocaleTimeString()}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ReferralManager;
